/**
 * Product Bundle ("Bundle & save")
 *
 * PDP block: up to 4 hand-picked products, each added to cart individually,
 * with quantity-break discount tiers previewed against the LIVE cart.
 *
 * The discount itself is NOT applied here — tiers are display-only messaging.
 * A matching Shopify Automatic Discount (cart-wide quantity break) configured
 * in admin applies the real saving at checkout.
 *
 * Adding to cart is the theme's own path: each row is a form and the button
 * carries data-add-to-cart, which CartDrawer's global click listener picks up,
 * so the drawer, loading state and error handling all come from the theme.
 * This file only handles the quantity steppers, the variant price swap, and
 * repainting progress when the cart changes.
 */
(function () {
  'use strict';

  /**
   * Formats cents into a money string via the shared tier engine.
   * @param {number} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    var BT = window.theme && window.theme.bundleTiers;
    return BT ? BT.formatMoney(Number.isFinite(cents) ? cents : 0) : '';
  }

  class ProductBundle extends HTMLElement {
    constructor() {
      super();
      this.tiers = [];
      this.cart = { item_count: 0, items_subtotal_price: 0 };
      this.onCartChanged = this.onCartChanged.bind(this);
    }

    connectedCallback() {
      this.parseConfig();
      this.bindRows();
      this.fetchCart();

      // Re-render tier progress when the cart changes from anywhere
      // (native add-to-cart, drawer quantity edits, etc.). Both events bubble.
      document.addEventListener('theme:cart:added', this.onCartChanged);
      document.addEventListener('theme:cart:update', this.onCartChanged);
    }

    disconnectedCallback() {
      document.removeEventListener('theme:cart:added', this.onCartChanged);
      document.removeEventListener('theme:cart:update', this.onCartChanged);
    }

    parseConfig() {
      var BT = window.theme && window.theme.bundleTiers;
      this.tiers = BT ? BT.parseTiers(this.getAttribute('data-tiers')) : [];
      this.progressTemplate = this.getAttribute('data-progress-template') || '';
      this.reachedTemplate = this.getAttribute('data-reached-template') || '';
      this.topReachedTemplate = this.getAttribute('data-top-reached-template') || '';
    }

    bindRows() {
      var self = this;
      this.querySelectorAll('[data-bundle-row]').forEach(function (row) {
        // Quantity steppers
        row.querySelectorAll('[data-qty-step]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            var step = parseInt(btn.getAttribute('data-qty-step'), 10) || 1;
            var input = row.querySelector('[data-bundle-qty]');
            if (!input) return;
            var current = parseInt(input.value, 10) || 1;
            input.value = String(Math.max(1, current + step));
          });
        });

        // Multi-variant: swap the displayed price when the size changes
        var sizeSelect = row.querySelector('[data-bundle-size]');
        var priceEl = row.querySelector('[data-bundle-price]');
        if (sizeSelect && priceEl && !priceEl.hasAttribute('data-from-price')) {
          sizeSelect.addEventListener('change', function () {
            var opt = sizeSelect.options[sizeSelect.selectedIndex];
            var priceCents = opt && opt.getAttribute('data-price');
            if (priceCents == null) return;
            var priceSpan = priceEl.querySelector('.money') || priceEl;
            priceSpan.textContent = formatMoney(parseInt(priceCents, 10));
          });
        }
      });
    }

    fetchCart() {
      var self = this;
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (res) {
          if (!res.ok) return null;
          return res.json();
        })
        .then(function (cart) {
          if (cart) {
            self.cart = cart;
            self.render();
          }
        })
        .catch(function () {
          /* silent — render keeps last known state */
        });
    }

    onCartChanged() {
      this.fetchCart();
    }

    render() {
      var BT = window.theme && window.theme.bundleTiers;
      if (!BT) return;
      var state = BT.resolve(this.cart.items_subtotal_price, this.tiers);
      this.renderProgress(state, BT);
      this.renderTrack();
    }

    // Same ticked-checkpoint track as the mini cart, via the shared renderer.
    renderTrack() {
      const BP = window.theme && window.theme.bundleProgress;
      const mount = this.querySelector('[data-bundle-track]');
      if (!BP || !mount) return;
      BP.render(mount, { total: this.cart.items_subtotal_price, tiers: this.tiers });
    }

    renderProgress(state, BT) {
      var progressEl = this.querySelector('[data-bundle-progress]');
      if (!progressEl) return;

      var current = state.current;
      var next = state.next;
      var html = '';

      if (next) {
        var nudge = BT.fillTemplate(this.progressTemplate, {
          amount: '<b>' + BT.formatMoneyShort(state.remainingCents) + '</b>',
          reward: '<b>' + BT.describeReward(next) + '</b>',
        });
        if (current) {
          // A tier is already unlocked, but a bigger one is still available.
          var reached = BT.fillTemplate(this.reachedTemplate, {
            reward: '<b>' + BT.describeReward(current) + '</b>',
          });
          html = reached ? reached + ' &middot; ' + nudge : nudge;
        } else {
          html = nudge;
        }
      } else if (current) {
        html = BT.fillTemplate(this.topReachedTemplate, {
          reward: '<b>' + BT.describeReward(current) + '</b>',
        });
      }

      progressEl.innerHTML = html;
    }

  }

  if (!customElements.get('product-bundle')) {
    customElements.define('product-bundle', ProductBundle);
  }
})();
